import React, { useCallback, useState } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';

const nodeTypes = {
  trigger: { label: '▶️ Trigger', color: '#10b981' },
  action: { label: '⚙️ Action', color: '#3b82f6' },
  condition: { label: '🔀 Condition', color: '#f59e0b' },
  end: { label: '✓ End', color: '#6b7280' },
};

const connectorOptions = [
  { id: 'epicor', label: 'Epicor ERP', icon: '📦' },
  { id: 'email', label: 'Email', icon: '📧' },
  { id: 'slack', label: 'Slack', icon: '💬' },
  { id: 'shopify', label: 'Shopify', icon: '🛒' },
  { id: 'database', label: 'Database', icon: '🗄️' },
];

export default function FlowBuilder({ workflowId, onSave }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([
    {
      id: '1',
      data: { label: 'Start Workflow' },
      position: { x: 250, y: 5 },
      type: 'trigger',
      style: { background: '#10b981', color: 'white', padding: '10px', borderRadius: '8px', fontWeight: 'bold' },
    },
  ]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeType, setSelectedNodeType] = useState('action');

  const onConnect = useCallback(
    (connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges],
  );

  const addNode = () => {
    const newNode = {
      id: `node-${Date.now()}`,
      data: { label: `New ${selectedNodeType}` },
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      type: selectedNodeType,
      style: {
        background: getNodeColor(selectedNodeType),
        color: 'white',
        padding: '10px',
        borderRadius: '8px',
        fontWeight: 'bold',
        minWidth: '120px',
        textAlign: 'center',
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const getNodeColor = (type) => {
    const colors = {
      trigger: '#10b981',
      action: '#3b82f6',
      condition: '#f59e0b',
      end: '#6b7280',
    };
    return colors[type] || '#3b82f6';
  };

  return (
    <div className="flex gap-4 h-96">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Node Types</h3>
          <div className="space-y-2">
            {Object.entries(nodeTypes).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setSelectedNodeType(key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedNodeType === key
                    ? 'bg-blue-100 text-blue-700 font-semibold'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={addNode}
          className="w-full btn btn-primary mb-6"
        >
          + Add {selectedNodeType}
        </button>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Connectors</h3>
          <div className="space-y-2">
            {connectorOptions.map((connector) => (
              <div
                key={connector.id}
                className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <span className="text-lg">{connector.icon}</span> {connector.label}
              </div>
            ))}
          </div>
        </div>

        <button onClick={onSave} className="w-full btn btn-primary mt-6">
          Save Workflow
        </button>
      </div>

      {/* Canvas */}
      <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
}
